import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { DriveFolderPicker } from "./DriveFolderPicker";
import { DiscoveryReview } from "./DiscoveryReview";
import { ReadinessList } from "./ReadinessList";
import { ProgressSteps } from "../chat/ProgressSteps";
import { useCreateBlankProject, useUpdateProject } from "../../hooks/useProjects";
import { CATEGORIES } from "./categories";
import { useDiscoverProject, useCommitDiscovery, useReadiness } from "../../hooks/useDiscovery";
import { useChatProgress, newRequestId } from "../../hooks/useChat";
import { errorMessage } from "../../hooks/useServers";

const ROW_ICON = { success: "✅", error: "❌", warning: "🟡", pending: "🟡" };

// Con carpeta de Drive: el panel la lee entera, muestra un resumen y, con un solo OK, deja el proyecto listo (servidores
// conectados, probados y analizados). Sin carpeta: proyecto NUEVO desde cero (taller): se crea su carpeta de Drive con la
// estructura estándar de documentos y las reglas de trabajo, y queda pendiente conectar el servidor del taller (candado 🔒).
export function NewProjectDialog({ open, onOpenChange, onProjectCreated }) {
  const [step, setStep] = useState("setup"); // setup | reading | review | preparing | done | blank_done
  const [name, setName] = useState("");
  const [driveFolder, setDriveFolder] = useState(null);
  const [category, setCategory] = useState(""); // tipo de proyecto (opcional): se aplica en cuanto el proyecto existe
  const [goal, setGoal] = useState("");
  const [description, setDescription] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [discovery, setDiscovery] = useState(null);
  const [excluded, setExcluded] = useState([]); // entradas que el usuario descartó en la revisión («No conectar»)
  const [result, setResult] = useState(null);
  const [blank, setBlank] = useState(null);
  const [error, setError] = useState("");
  const [requestId, setRequestId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);

  const createBlank = useCreateBlankProject();
  const updateProject = useUpdateProject();
  const discoverProject = useDiscoverProject();
  const commitDiscovery = useCommitDiscovery();
  const working = step === "reading" || step === "preparing";
  const { data: progress } = useChatProgress(requestId, working);
  const projectId = result?.project?.id || blank?.project?.id;
  const { data: readiness } = useReadiness(step === "done" ? result?.project?.id : null, { poll: true });

  const reset = () => {
    setStep("setup");
    setName("");
    setDriveFolder(null);
    setCategory("");
    setGoal("");
    setDescription("");
    setSystemPrompt("");
    setDiscovery(null);
    setResult(null);
    setBlank(null);
    setError("");
    setRequestId(null);
  };

  const handleOpenChange = (next) => {
    if (!next) {
      const created = (step === "done" || step === "blank_done") && projectId;
      reset();
      onOpenChange(false);
      if (created) onProjectCreated(projectId);
      return;
    }
    onOpenChange(next);
  };

  const startWork = (nextStep) => {
    const rid = newRequestId();
    setRequestId(rid);
    setStartedAt(Date.now());
    setError("");
    setStep(nextStep);
    return rid;
  };

  const handleContinue = async () => {
    if (!name.trim()) return;
    setError("");
    if (!driveFolder) {
      try {
        const created = await createBlank.mutateAsync({ name: name.trim(), goal: goal.trim() });
        if (category && created?.project?.id) updateProject.mutate({ id: created.project.id, category });
        setBlank(created);
        setStep("blank_done");
      } catch (e) {
        setError(errorMessage(e, "No se pudo crear el proyecto."));
      }
      return;
    }
    const rid = startWork("reading");
    try {
      const found = await discoverProject.mutateAsync({ name: name.trim(), driveFolder, requestId: rid });
      setDiscovery(found);
      setExcluded([]);
      setDescription(found.description || "");
      setSystemPrompt(found.system_prompt || "");
      setStep("review");
    } catch (e) {
      setError(errorMessage(e, "No pude leer la carpeta. Inténtalo de nuevo."));
      setStep("setup");
    }
  };

  const handleCommit = async () => {
    const rid = startWork("preparing");
    try {
      const done = await commitDiscovery.mutateAsync({
        sessionId: discovery.session_id,
        name: name.trim(),
        description: description.trim(),
        systemPrompt: systemPrompt.trim(),
        requestId: rid,
        excludeKeys: excluded,
      });
      if (category && done?.project?.id) updateProject.mutate({ id: done.project.id, category });
      setResult(done);
      setStep("done");
    } catch (e) {
      setError(errorMessage(e, "No pude dar de alta el proyecto."));
      setStep("review");
    }
  };

  const descriptions = {
    setup: "Nombre del proyecto. Con una carpeta de Drive, el panel la lee entera y prepara todo; sin carpeta, empieza un proyecto nuevo desde cero.",
    reading: "Leyendo la carpeta y buscando servidores…",
    review: "Esto es lo que encontré. Si está bien, da OK y el proyecto queda listo.",
    preparing: "Dando de alta el proyecto y conectando los servidores…",
    done: "Proyecto creado.",
    blank_done: "Proyecto nuevo creado.",
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="new-project-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nuevo proyecto</DialogTitle>
          <DialogDescription>{descriptions[step]}</DialogDescription>
        </DialogHeader>

        {step === "setup" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-project-name">Nombre</Label>
              <Input
                id="new-project-name"
                data-testid="new-project-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej. INALAM"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-project-category">Tipo de proyecto (opcional)</Label>
              <select
                id="new-project-category"
                data-testid="new-project-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">Sin tipo</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Carpeta en SISTEMAS (Drive)</Label>
              <DriveFolderPicker value={driveFolder} onChange={setDriveFolder} />
            </div>
            {!driveFolder && (
              <div className="space-y-1.5">
                <Label htmlFor="new-project-goal">¿Qué quieres construir? (opcional; puedes definirlo después en el chat)</Label>
                <Textarea
                  id="new-project-goal"
                  data-testid="new-project-goal"
                  rows={4}
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="Ej. Un sistema para llevar el control de pagos de mis clientes, con panel web y avisos por WhatsApp."
                />
              </div>
            )}
          </div>
        )}

        {working && (
          <div data-testid="discovery-working">
            <ProgressSteps steps={progress?.steps} startedAt={startedAt} />
          </div>
        )}

        {step === "review" && discovery && (
          <DiscoveryReview
            discovery={discovery}
            description={description}
            systemPrompt={systemPrompt}
            onDescription={setDescription}
            onSystemPrompt={setSystemPrompt}
            excluded={excluded}
            onToggleExclude={(key) => setExcluded((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]))}
          />
        )}

        {step === "done" && result && (
          <div data-testid="discovery-done" className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <p data-testid="discovery-summary" className="text-sm font-medium">{result.summary}</p>
            {result.rows.map((row, i) => (
              <p key={i} className="text-xs text-muted-foreground">{ROW_ICON[row.status] || "🟡"} <span className="font-medium text-foreground">{row.name}</span> — {row.result}</p>
            ))}
            {result.texts.map((t, i) => (
              <p key={i} className="text-xs text-muted-foreground">{t}</p>
            ))}
            <ReadinessList readiness={readiness} />
          </div>
        )}

        {step === "blank_done" && blank && (
          <div data-testid="blank-done" className="space-y-3 max-h-[60vh] overflow-y-auto pr-1 text-sm">
            <p data-testid="blank-summary" className="font-medium">✅ Creé «{blank.project.name}» desde cero.</p>
            <p className="text-xs text-muted-foreground">
              En Drive: carpeta <span className="font-mono">{blank.folder}</span> con {blank.files.length} documentos (reglas de trabajo, estado, mapa, historial, pendientes,
              seguridad, decisiones, backups y glosario).
            </p>
            {blank.taller?.connected ? (
              <div data-testid="blank-taller-ok" className="rounded-lg border border-emerald-500/40 p-3 space-y-1">
                <p className="text-xs font-medium text-emerald-500">✅ Conectado al taller</p>
                <p className="text-xs text-muted-foreground">
                  Tu carpeta de trabajo es <span className="font-mono">{blank.taller.folder || blank.base_dir}</span>, con usuario propio. El Orquestador la usará solo cuando construya algo.
                </p>
              </div>
            ) : (
              <div data-testid="blank-next" className="rounded-lg border border-amber-500/40 p-3 space-y-1">
                <p className="text-xs font-medium text-amber-500">Siguiente paso: conectar el servidor del taller (una sola vez)</p>
                <p className="text-xs text-muted-foreground">
                  El código vivirá en <span className="font-mono">{blank.base_dir}</span> de ese servidor, con versionado automático. Captura sus datos (IP, usuario y contraseña) en el botón «Taller» de la pantalla de proyectos.
                </p>
                {blank.taller?.message && <p className="text-xs text-muted-foreground">{blank.taller.message}</p>}
              </div>
            )}
          </div>
        )}

        {error && <p data-testid="discovery-error" className="text-sm text-red-500">{error}</p>}

        <DialogFooter>
          {step === "setup" && (
            <Button data-testid="project-draft-button" onClick={handleContinue} disabled={!name.trim() || createBlank.isPending}>
              {createBlank.isPending ? "Creando..." : driveFolder ? "Leer la carpeta" : "Crear proyecto nuevo"}
            </Button>
          )}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={() => { setError(""); setStep("setup"); }}>
                Volver
              </Button>
              <Button data-testid="discovery-commit-button" onClick={handleCommit}>
                Aceptar y preparar el proyecto
              </Button>
            </>
          )}
          {(step === "done" || step === "blank_done") && (
            <Button data-testid="discovery-open-button" onClick={() => handleOpenChange(false)}>
              Abrir el proyecto
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
